import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import PracticeArea from '@/pages/lesson/components/PracticeArea';
import FoundationCarousel from '@/pages/lesson/components/FoundationCarousel';
import InstrumentPractice from '@/pages/lesson/components/InstrumentPractice';
import ClefQuiz from '@/pages/lesson/components/ClefQuiz';
import RhythmPractice from '@/pages/lesson/components/RhythmPractice';
import AttemptHistoryPanel from '@/pages/lesson/components/AttemptHistoryPanel';
import StudentLevelCard from '@/components/feature/StudentLevelCard';
import { useProgress } from '@/hooks/useProgress';
import { useAuth } from '@/context/AuthContext';
import { SIGN_QUESTIONS } from '@/data/signsQuiz';
import { ACCIDENTAL_QUESTIONS } from '@/data/accidentalsQuiz';
import { BASS_CLEF_QUESTIONS } from '@/data/bassClefQuiz';
import { accuracy as scoreAccuracy, registerNoteResult, type ScoreState } from '@/lib/exercise/engine';
import type { ExerciseSummary, RhythmScore } from '@/lib/exercise/rhythmEngine';
import { itemsFromRhythmScore, type AttemptRecord } from '@/lib/progress/history';
import { assessRhythmAttempt } from '@/lib/exercise/rhythmSummary';
import { isLessonUnlocked, STREAK_GOAL } from '@/lib/progress/gating';
import {
  getModule,
  nextLessonId,
  previousLessonId,
  type Lesson,
} from '@/data/lessons';

interface LessonContentProps {
  lesson: Lesson;
}

export default function LessonContent({ lesson }: LessonContentProps) {
  const { user } = useAuth();
  const reviewAccess = user?.accessLevel === 'review' || user?.accessLevel === 'admin';
  const {
    progress,
    getLessonProgress,
    updateLesson,
    attempts,
    addAttempt,
    clearAttempts,
    serverReady,
  } = useProgress();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [instrumentPractice, setInstrumentPractice] = useState(false);
  const supportsInstrumentPractice = (lesson.mode === 'note' || lesson.mode === 'line-space') && Boolean(lesson.notes?.length);

  const lessonProgress = getLessonProgress(lesson.id);
  const module = getModule(lesson.moduleId);
  const prev = previousLessonId(lesson.id);
  const next = nextLessonId(lesson.id);
  const unlocked = reviewAccess || isLessonUnlocked(progress, lesson.id);
  const nextUnlocked = next !== null && (reviewAccess || isLessonUnlocked(progress, next));

  const hasRests = (lesson.rhythm?.rests ?? []).length > 0;
  const hasTies = lesson.rhythm?.ties === true;
  const measuresDuration = lesson.rhythm?.responseMode === 'on-beat';

  /** Grava um registro no histórico local (data e resultado vêm do hook). */
  const pushAttempt = useCallback(
    (record: Omit<AttemptRecord, 'id' | 'at'>) => {
      addAttempt(record);
    },
    [addAttempt],
  );

  const handleNoteResult = useCallback(
    (noteId: string, correct: boolean) => {
      updateLesson(lesson.id, (previous) => ({
        ...previous,
        attempts: previous.attempts + 1,
        correct: previous.correct + (correct ? 1 : 0),
        wrong: previous.wrong + (correct ? 0 : 1),
        noteStats: registerNoteResult(previous.noteStats, noteId, correct),
      }));
    },
    [lesson.id, updateLesson],
  );

  // ── Reconhecimento de notas / linhas e espaços ────────────────────────────
  const handleFinish = useCallback(
    (score: ScoreState) => {
      updateLesson(lesson.id, (previous) => ({
        ...previous,
        bestStreak: Math.max(previous.bestStreak, score.bestStreak, STREAK_GOAL),
        mastered: true,
      }));
      pushAttempt({
        lessonId: lesson.id,
        scope: 'lesson',
        result: 'mastered',
        accuracy: scoreAccuracy(score),
        items: { note: { correct: score.correct, wrong: score.wrong } },
      });
    },
    [lesson.id, pushAttempt, updateLesson],
  );

  const handleRecognitionAbort = useCallback(
    (score: ScoreState) => {
      if (score.total === 0) return;
      pushAttempt({
        lessonId: lesson.id,
        scope: 'lesson',
        result: 'incomplete',
        accuracy: scoreAccuracy(score),
        items: { note: { correct: score.correct, wrong: score.wrong } },
      });
    },
    [lesson.id, pushAttempt],
  );

  // ── Ritmo / execução / melodia ────────────────────────────────────────────
  // A aula só é dominada (e desbloqueia a seguinte) com uma execução COMPLETA
  // sem erros nos itens que a aula ensina. A precisão em % é apenas informativa.
  const handleRhythmFinish = useCallback(
    (acc: number, score: RhythmScore, history: ExerciseSummary[]) => {
      const context = { hasRests, hasTies, measuresDuration };
      const assessment = assessRhythmAttempt(score, context);
      updateLesson(lesson.id, (previous) => ({
        ...previous,
        mastered: previous.mastered || assessment.mastered,
      }));
      pushAttempt({
        lessonId: lesson.id,
        scope: 'lesson',
        result: assessment.mastered ? 'mastered' : assessment.errorFree ? 'clean' : 'completed',
        accuracy: acc,
        items: itemsFromRhythmScore(score, hasRests, hasTies, measuresDuration),
      });
      history.forEach((entry) => {
        const entryAssessment = assessRhythmAttempt(entry.score, context);
        pushAttempt({
          lessonId: lesson.id,
          scope: 'exercise',
          exerciseIndex: entry.index,
          timeSignature: entry.timeSignature,
          result: entryAssessment.errorFree ? 'clean' : 'completed',
          items: itemsFromRhythmScore(entry.score, hasRests, hasTies, measuresDuration),
        });
      });
    },
    [hasRests, hasTies, lesson.id, measuresDuration, pushAttempt, updateLesson],
  );

  const handleRhythmAbort = useCallback(
    (score: RhythmScore, history: ExerciseSummary[]) => {
      if (score.attempts === 0 && history.length === 0) return;
      pushAttempt({
        lessonId: lesson.id,
        scope: 'lesson',
        result: 'incomplete',
        accuracy: score.attempts > 0 ? Math.round((score.correct / score.attempts) * 100) : 0,
        items: itemsFromRhythmScore(score, hasRests, hasTies, measuresDuration),
      });
    },
    [hasRests, hasTies, lesson.id, measuresDuration, pushAttempt],
  );

  // ── Aulas de explicação (quiz) ────────────────────────────────────────────
  const quizQuestions =
    lesson.quiz === 'signs'
      ? SIGN_QUESTIONS
      : lesson.quiz === 'accidentals'
        ? ACCIDENTAL_QUESTIONS
        : lesson.quiz === 'bass-clef'
          ? BASS_CLEF_QUESTIONS
          : undefined;

  const handleQuizAnswer = useCallback(
    (correct: boolean) => {
      updateLesson(lesson.id, (previous) => ({
        ...previous,
        attempts: previous.attempts + 1,
        correct: previous.correct + (correct ? 1 : 0),
        wrong: previous.wrong + (correct ? 0 : 1),
      }));
    },
    [lesson.id, updateLesson],
  );

  const handleQuizComplete = useCallback(
    (passed: boolean, correct: number, total: number) => {
      if (passed) {
        updateLesson(lesson.id, (previous) => ({ ...previous, mastered: true }));
      }
      pushAttempt({
        lessonId: lesson.id,
        scope: 'lesson',
        result: passed ? 'mastered' : 'completed',
        accuracy: total > 0 ? Math.round((correct / total) * 100) : 0,
        items: { note: { correct, wrong: Math.max(0, total - correct) } },
      });
    },
    [lesson.id, pushAttempt, updateLesson],
  );

  if (!serverReady) {
    return <div className="mx-auto max-w-5xl px-4 py-12 text-center text-slate-600">Carregando seu progresso...</div>;
  }

  if (instrumentPractice && unlocked && supportsInstrumentPractice && lesson.notes) {
    return <InstrumentPractice key={lesson.id} noteIds={lesson.notes} lessonTitle={`Aula ${lesson.id} — ${lesson.title}`} onClose={() => setInstrumentPractice(false)} />;
  }

  if (lesson.id >= 1 && lesson.id <= 4 && unlocked) {
    return (
      <div className="w-full">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-3 pt-4 sm:px-4">
          <StudentLevelCard progress={progress} compact />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" icon="ri-music-2-line" onClick={() => setInstrumentPractice(true)}>
              Praticar no meu piano
            </Button>
            <Button variant="outline" size="sm" icon="ri-history-line" onClick={() => setHistoryOpen(true)}>
              Meu histórico
            </Button>
          </div>
        </div>
        <FoundationCarousel
          lessonId={lesson.id}
          reviewAccess={reviewAccess}
          progress={lessonProgress}
          updateProgress={(updater) => updateLesson(lesson.id, updater)}
          onAttempt={(correct) => pushAttempt({
            lessonId: lesson.id,
            scope: 'exercise',
            result: correct ? 'clean' : 'incomplete',
            accuracy: correct ? 100 : 0,
            items: { note: { correct: correct ? 1 : 0, wrong: correct ? 0 : 1 } },
          })}
        />
        <AttemptHistoryPanel
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          lessonId={lesson.id}
          attempts={attempts}
          onClear={clearAttempts}
        />
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-12">
      <div className="mx-auto max-w-6xl">
        {/* Navegação de contexto */}
        <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
          <Link to="/" className="hover:text-primary-700 cursor-pointer">
            Início
          </Link>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
          <Link to="/aulas" className="hover:text-primary-700 cursor-pointer">
            Aulas
          </Link>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
          <span className="text-foreground-800">Aula {lesson.id}</span>
        </nav>

        {/* Cabeçalho da aula */}
        <header className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="primary" icon="ri-stack-line">
                Módulo {lesson.moduleId} · {module?.title}
              </Badge>
              {lessonProgress.mastered ? (
                <Badge tone="accent" icon="ri-medal-line">
                  Aula dominada
                </Badge>
              ) : unlocked ? (
                <Badge tone="primary" icon="ri-play-circle-line">
                  Disponível para praticar
                </Badge>
              ) : (
                <Badge tone="secondary" icon="ri-lock-2-line">
                  Bloqueada
                </Badge>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <StudentLevelCard progress={progress} compact />
              {unlocked && supportsInstrumentPractice && (
                <Button size="sm" icon="ri-music-2-line" onClick={() => setInstrumentPractice(true)}>
                  Praticar no meu piano
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                icon="ri-history-line"
                onClick={() => setHistoryOpen(true)}
              >
                Meu histórico
              </Button>
            </div>
          </div>

          <h1 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            Aula {lesson.id} — {lesson.title}
          </h1>
          <p className="mt-2 max-w-3xl text-foreground-700">{lesson.objective}</p>

          {lessonProgress.attempts > 0 ? (
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-sm text-foreground-600">
              <span>
                Tentativas: <strong className="text-foreground-900">{lessonProgress.attempts}</strong>
              </span>
              <span>
                Acertos: <strong className="text-accent-700">{lessonProgress.correct}</strong>
              </span>
              <span>
                Erros: <strong className="text-secondary-700">{lessonProgress.wrong}</strong>
              </span>
              <span>
                Melhor sequência:{' '}
                <strong className="text-foreground-900">{lessonProgress.bestStreak}</strong>
              </span>
            </div>
          ) : null}
        </header>

        {/* Conteúdo */}
        <div className="mt-6">
          {!unlocked ? (
            <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-10">
              <div className="flex items-center gap-3">
                <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                  <i className="ri-lock-2-line text-2xl" />
                </span>
                <div>
                  <h2 className="font-heading text-xl font-bold text-foreground-950">
                    Aula bloqueada
                  </h2>
                  <p className="text-sm text-foreground-600">
                    O curso avança em sequência: domine a aula {prev} para desbloquear esta.
                  </p>
                </div>
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-2">
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="font-label text-xs font-bold uppercase tracking-wide text-foreground-500">
                    O que esta aula ensina
                  </span>
                  <p className="mt-2 text-sm text-foreground-800">{lesson.objective}</p>
                  <p className="mt-3 text-sm text-foreground-600">{lesson.explanation}</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="font-label text-xs font-bold uppercase tracking-wide text-foreground-500">
                    Como desbloquear
                  </span>
                  <p className="mt-2 text-sm text-foreground-800">
                    Conclua a aula anterior cumprindo o critério de domínio dela. O desbloqueio
                    fica salvo na sua conta.
                  </p>
                  {prev ? (
                    <div className="mt-4">
                      <Link
                        to={`/aulas/${prev}`}
                        className="inline-flex items-center gap-2 text-sm font-label font-semibold text-primary-700 hover:text-primary-800 cursor-pointer"
                      >
                        <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
                        Ir para a aula {prev}
                      </Link>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ) : lesson.mode === 'rhythm' && lesson.rhythm ? (
            <RhythmPractice
              lesson={lesson}
              noteStats={lessonProgress.noteStats}
              onNoteResult={handleNoteResult}
              onFinish={handleRhythmFinish}
              onAbort={handleRhythmAbort}
            />
          ) : lesson.mode === 'clef-quiz' ? (
            <ClefQuiz
              onAnswer={handleQuizAnswer}
              onComplete={handleQuizComplete}
              questions={quizQuestions}
            />
          ) : (
            <PracticeArea
              lesson={lesson}
              noteStats={lessonProgress.noteStats}
              onNoteResult={handleNoteResult}
              onFinish={handleFinish}
              onAbort={handleRecognitionAbort}
            />
          )}
        </div>

        {/* Navegação entre aulas */}
        <div className="mt-8 flex flex-col gap-3 border-t border-background-200 pt-6 sm:flex-row sm:justify-between">
          {prev ? (
            <Link
              to={`/aulas/${prev}`}
              className="inline-flex items-center gap-2 text-sm font-label font-semibold text-foreground-800 hover:text-primary-700 cursor-pointer"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
              Aula {prev}
            </Link>
          ) : (
            <span />
          )}
          {next && nextUnlocked ? (
            <Link
              to={`/aulas/${next}`}
              className="inline-flex items-center gap-2 text-sm font-label font-semibold text-foreground-800 hover:text-primary-700 cursor-pointer sm:justify-end"
            >
              Aula {next}
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </Link>
          ) : next ? (
            <span className="inline-flex items-center gap-2 text-sm font-label font-semibold text-foreground-400 sm:justify-end">
              <i className="ri-lock-2-line w-4 h-4 flex items-center justify-center" />
              Aula {next} bloqueada
            </span>
          ) : (
            <span />
          )}
        </div>

        <div className="mt-6">
          <Link
            to="/aulas"
            className="inline-flex items-center gap-2 text-sm font-label font-semibold text-primary-700 hover:text-primary-800 cursor-pointer"
          >
            <i className="ri-list-check w-4 h-4 flex items-center justify-center" />
            Ver todas as 48 aulas
          </Link>
        </div>
      </div>

      <AttemptHistoryPanel
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        lessonId={lesson.id}
        attempts={attempts}
        onClear={clearAttempts}
      />
    </div>
  );
}